// The delta-v map's wide window: the picked trip's flight plan step by step,
// then every trip the map can plan, each with all of its figures, laid out for
// a width the rail panel does not have.
//
// Opened and shut from the slim panel (src/hudDvMap.ts), which stays beside it
// and pressable, so GO, CANCEL and WARP TO NEXT never go behind it. While it
// is open no key reaches the game: a capture-phase listener takes every
// keydown before any other window listener sees it, and the `dvtable` mode
// flag (src/appMode.ts) keeps the wheel and the mouse-look off the camera.

import { setMode } from './appMode';
import type { DvMapFlight } from './hudDvMap';
import {
  FREE_TIP, INSTANT_TIP, MOVED_TIP, NOTE_TIP, NOTHING, NO_GO_TIP, PHASE_WORDS, RELATIVE_TIP,
  SKIPPED_TIP, TEXTBOOK_TIP, TO, WAIT_TIP, WHEN_TIP, WORKING_TIP, burnFigure, burnTip, dateText,
  days, durationText, dvText, goOffered, heightText, kms, moonGoTip, movedRowText, paintPlan,
  plainBodyName, planRows, planTip, spentText, tiltTip, tip, turnTip, waitText, SENSE_WORDS,
} from './hudDvText';
import type { PlanCol } from './hudDvText';
import type { DvMap, DvMapState } from './sim/dvMap';
import type { TransferPlan, TransferSkip } from './sim/transfer';
import { TRANSFER_ASSUMPTIONS_SUMMARY } from './sim/transfer';
import { TEST_SWITCHES } from './urlFlags.ts';

/**
 * The control (`?dvmapleavesoutlong=1`): put "WHAT THIS LEAVES OUT" back to
 * every engineering assumption line instead of the fixed two-line summary -
 * the ramble the owner asked cut. The leaves-out reading has to catch it.
 */
const LEAVES_OUT_LONG = TEST_SWITCHES && new URLSearchParams(location.search).get('dvmapleavesoutlong') === '1';

/**
 * The control (`?dvtableheadclear=1`): the column header back on the HUD's
 * see-through background, so the rows scrolling under it show through.
 * tools/preview/dvmap-panel-fit.mjs's header leg has to catch it.
 */
const HEAD_CLEAR = TEST_SWITCHES && new URLSearchParams(location.search).get('dvtableheadclear') === '1';

/**
 * The control (`?dvtableheadunder=1`): the header kept opaque but stacked
 * under the rows, so a press on it lands on whatever row has scrolled there.
 * The header leg's hit reading has to catch what its colour reading cannot.
 */
const HEAD_UNDER = TEST_SWITCHES && new URLSearchParams(location.search).get('dvtableheadunder') === '1';

/** The window's flight plan carries every column. */
const WIDE_PLAN_COLS: readonly PlanCol[] = ['step', 'at', 'dv', 'lasts', 'how'];

interface DvTableConfig {
  map: DvMap;
  /** The thrust a trip's burns are flown at, m/s2, for the burn durations. */
  accelMps2?: () => number;
  /** Share of full thrust that is, under the radiators; 1 where absent. */
  thrustShare?: () => number;
  /** The tooltip text of a glossary row, by its id, for each column's cells. */
  glossaryTip: (rowId: string) => string;
  /** Told whenever the window opens or shuts, so the control that opens it
   *  can say which. */
  onOpenChange?: (open: boolean) => void;
}

export interface DvTable {
  isOpen: () => boolean;
  setOpen: (open: boolean) => void;
  /** The map's own state, on every change of it. */
  paint: (state: DvMapState) => void;
  /** The autopilot's own line, once a frame; null while nothing flies. */
  setFlight: (flight: DvMapFlight | null) => void;
  /** The COURSE FIX row's text, once a frame. */
  setCorrection: (text: string) => void;
  dispose: () => void;
}

function req<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`hudDvTable: no #${id} in the page`);
  return el as T;
}

/** One row's columns in order: the figure spans `paintRow` fills, the words
 *  the narrow layout prints over each, and the glossary row whose tooltip a
 *  cell with none of its own carries. */
const ROW_CELLS: ReadonlyArray<{ col: string; label: string; glossary: string; facts: readonly string[] }> = [
  { col: 'name', label: 'WHERE TO', glossary: 'dv-col-dest', facts: ['name', 'full-name'] },
  { col: 'total', label: 'SPEED CHANGE', glossary: 'dv-col-total', facts: ['total'] },
  { col: 'tof', label: 'TRAVEL TIME', glossary: 'dv-col-tof', facts: ['tof'] },
  { col: 'wait', label: 'LEAVES IN', glossary: 'dv-col-window', facts: ['wait'] },
  { col: 'leave', label: 'LEAVE', glossary: 'dv-col-leave', facts: ['leave', 'leave-how', 'instant'] },
  { col: 'arrive', label: 'ARRIVE', glossary: 'dv-col-arrive', facts: ['arrive', 'arrive-how'] },
  { col: 'past', label: 'SPEED PAST ON ARRIVAL', glossary: 'dv-col-past', facts: ['relative'] },
  { col: 'free', label: 'IN OPEN SPACE', glossary: 'dv-col-free', facts: ['free'] },
  { col: 'textbook', label: 'FLAT CIRCLES', glossary: 'dv-col-textbook', facts: ['textbook', 'textbook-tof'] },
  { col: 'dates', label: 'LEAVES / ARRIVES', glossary: 'dv-col-dates', facts: ['dates'] },
  { col: 'plane', label: 'MOON PLANE', glossary: 'dv-col-plane', facts: ['tilt', 'turn', 'autopilot'] },
];

/** Which push a figure span is, for a reader that asks by burn kind. */
const BURN_FACTS: Readonly<Record<string, string>> = { leave: 'departure', arrive: 'capture' };

function buildRow(glossaryTip: (rowId: string) => string): HTMLButtonElement {
  const row = document.createElement('button');
  row.type = 'button';
  row.className = 'dvt-row';
  for (const { col, label, glossary, facts } of ROW_CELLS) {
    const cellEl = document.createElement('span');
    cellEl.className = 'dvt-cell';
    cellEl.dataset['col'] = col;
    cellEl.dataset['label'] = label;
    const why = glossaryTip(glossary);
    if (why !== '') tip(cellEl, why);
    facts.forEach((fact, i) => {
      const span = document.createElement('span');
      span.className = i === 0 ? 'dvt-main' : 'dvt-sub';
      span.dataset['fact'] = fact;
      const kind = BURN_FACTS[fact];
      if (kind !== undefined) span.dataset['kind'] = kind;
      cellEl.append(span);
    });
    row.append(cellEl);
  }
  return row;
}

function factEl(row: HTMLElement, fact: string): HTMLElement | null {
  return row.querySelector<HTMLElement>(`[data-fact="${fact}"]`);
}

/** Write one figure, and hide its span when there is nothing to say. */
function put(row: HTMLElement, fact: string, text: string, why: string | null = null): void {
  const el = factEl(row, fact);
  if (!el) return;
  if (el.textContent !== text) el.textContent = text;
  el.classList.toggle('hidden', text === '');
  if (why === null || text === '') {
    el.classList.remove('hud-tip');
    delete el.dataset['tooltip'];
  } else {
    tip(el, why);
  }
}

function paintRow(
  row: HTMLButtonElement, plan: TransferPlan, selected: boolean, working: boolean, accelMps2: number,
  share: number,
): void {
  row.dataset['dest'] = plan.destinationId;
  row.classList.toggle('dvt-row-open', selected);
  row.classList.toggle('dvt-row-working', working);
  row.setAttribute('aria-pressed', selected ? 'true' : 'false');
  const name = plainBodyName(plan.destinationName);
  put(row, 'name', name, planTip(plan, working));
  put(row, 'full-name', name === plan.destinationName ? '' : plan.destinationName);
  put(row, 'total', kms(plan.totalDvKmS), working ? WORKING_TIP : null);
  factEl(row, 'total')?.classList.toggle('dv-row-stale', working);
  put(row, 'tof', days(plan.timeOfFlightDays));
  put(row, 'wait', waitText(plan.windowWaitDays), WAIT_TIP);

  const departure = plan.burns.find((burn) => burn.kind === 'departure');
  const arrival = plan.burns.find((burn) => burn.kind === 'capture');
  if (departure === undefined) {
    put(row, 'leave', NOTHING);
    put(row, 'leave-how', '');
    put(row, 'instant', '');
  } else {
    put(row, 'leave', burnFigure(departure, accelMps2), burnTip(departure, accelMps2, share));
    put(row, 'leave-how', SENSE_WORDS[departure.sense]);
    put(row, 'instant', departure.dvKmS > departure.impulseDvKmS
      ? `instant ${kms(departure.impulseDvKmS)}` : '', INSTANT_TIP);
  }
  if (arrival === undefined) {
    put(row, 'arrive', NOTHING);
    put(row, 'arrive-how', '');
    put(row, 'relative', NOTHING);
  } else {
    put(row, 'arrive', burnFigure(arrival, accelMps2), burnTip(arrival, accelMps2, share));
    put(row, 'arrive-how', `${arrival.frame === 'heliocentric' ? 'match velocity' : 'capture'}, `
      + SENSE_WORDS[arrival.sense]);
    put(row, 'relative', kms(arrival.vInfKmS), RELATIVE_TIP);
  }
  put(row, 'free', kms(plan.heliocentricDvKmS), FREE_TIP);
  put(row, 'textbook', kms(plan.textbookTotalDvKmS), TEXTBOOK_TIP);
  put(row, 'textbook-tof', days(plan.textbookTimeOfFlightDays), TEXTBOOK_TIP);
  put(row, 'dates', `${dateText(plan.departureEpochJd)} ${TO} ${dateText(plan.arrivalEpochJd)}`, WHEN_TIP);

  // Under a twentieth of a degree the tilt would print as "0.0 deg".
  const tiltDeg = plan.planeTiltDeg;
  const turnEnd = plan.planeChangeEnd;
  const tilted = tiltDeg !== null && tiltDeg >= 0.05;
  put(row, 'tilt', tilted ? `${tiltDeg.toFixed(1)} deg off ${name}'s plane` : '',
    tilted ? tiltTip(name, tiltDeg) : null);
  put(row, 'turn', tilted && turnEnd !== null
    ? `turn ${kms(plan.planeChangeDvKmS)}, paid ${turnEnd === 'departure' ? 'leaving' : 'arriving'}` : '',
  tilted && turnEnd !== null ? turnTip(name, turnEnd) : null);
  // What GO does with a moon's trip, or why it is not offered; a trip
  // between worlds says nothing here.
  const auto = factEl(row, 'autopilot');
  if (plan.planeTiltDeg === null) {
    put(row, 'autopilot', '');
    if (auto) delete auto.dataset['go'];
  } else if (goOffered(plan)) {
    put(row, 'autopilot', `GO flies the crossing to ${name}`,
      moonGoTip(name, plan.planeChangeEnd, plan.totalDvKmS));
    if (auto) auto.dataset['go'] = 'offered';
  } else {
    put(row, 'autopilot', 'yours to fly', NO_GO_TIP);
    if (auto) auto.dataset['go'] = 'withheld';
  }
  if (factEl(row, 'tilt')?.textContent === '' && factEl(row, 'autopilot')?.textContent === '') {
    put(row, 'tilt', NOTHING);
  }
}

/** The same in-place list update the slim panel's list uses: a row a click is
 *  already travelling toward is never replaced by a survey chunk landing. */
function paintRows(
  list: HTMLElement, plans: readonly TransferPlan[], selectedId: string | null,
  working: ReadonlySet<string>, accelMps2: number, share: number, glossaryTip: (rowId: string) => string,
): void {
  const existing = new Map<string, HTMLButtonElement>();
  for (const child of Array.from(list.children)) {
    const id = (child as HTMLElement).dataset['dest'];
    if (id !== undefined) existing.set(id, child as HTMLButtonElement);
  }
  let after: ChildNode | null = null;
  for (const plan of plans) {
    let row = existing.get(plan.destinationId) ?? null;
    if (row === null) row = buildRow(glossaryTip);
    else existing.delete(plan.destinationId);
    paintRow(row, plan, plan.destinationId === selectedId, working.has(plan.destinationId), accelMps2, share);
    const wantNext: ChildNode | null = after === null ? list.firstChild : after.nextSibling;
    if (wantNext !== row) list.insertBefore(row, wantNext);
    after = row;
  }
  for (const stale of existing.values()) stale.remove();
}

/** One line of the cannot-get-there list: the body, then its reason. The
 *  window has the width for the whole reason, so it wraps rather than clips. */
function skipLine(skip: TransferSkip): HTMLElement {
  const line = document.createElement('div');
  line.className = 'dv-skip';
  line.dataset['id'] = skip.id;
  const name = document.createElement('span');
  name.className = 'dv-skip-name';
  name.textContent = plainBodyName(skip.name);
  const why = document.createElement('span');
  why.className = 'dv-skip-why';
  why.textContent = skip.reason;
  line.append(name, why);
  tip(line, `${skip.name} - ${skip.reason}`);
  return line;
}

export function createDvTable(config: DvTableConfig): DvTable {
  const { map } = config;
  const root = req<HTMLElement>('dv-table');
  const backdrop = req<HTMLElement>('dv-table-backdrop');
  const elClose = req<HTMLButtonElement>('dv-table-close');
  const elLive = req<HTMLButtonElement>('dv-origin-live');
  const elParking = req<HTMLButtonElement>('dv-origin-parking');
  const elFrom = req<HTMLElement>('dv-map-from');
  const elWhen = req<HTMLElement>('dv-map-when');
  const elMoved = req<HTMLElement>('dv-table-moved');
  const elNote = req<HTMLElement>('dv-table-note');
  const elList = req<HTMLElement>('dv-table-list');
  const elSkipped = req<HTMLElement>('dv-map-skipped');
  const elAssume = req<HTMLElement>('dv-map-assumptions');
  const elTrip = req<HTMLElement>('dv-table-trip');
  const elFix = req<HTMLElement>('dv-table-fix');
  const elFixRow = req<HTMLElement>('dv-table-fix-row');
  const elNextStepRow = req<HTMLElement>('dv-table-next-step-row');
  const elWhy = req<HTMLElement>('dv-table-why');
  const elPlan = req<HTMLElement>('dv-table-plan');
  const elPlanName = req<HTMLElement>('dv-table-plan-name');
  const elPlanList = req<HTMLElement>('dv-table-plan-list');
  const flyRows = Array.from(root.querySelectorAll<HTMLElement>('[data-fly-row]'));
  const fly = (key: string): HTMLElement | null => root.querySelector<HTMLElement>(`[data-fly="${key}"]`);

  let open = false;
  let flyKey = '';
  let fixText = '';
  let flying = false;

  tip(elNote, NOTE_TIP);
  tip(elMoved, MOVED_TIP);
  if (HEAD_CLEAR) root.classList.add('dvt-head-clear');
  if (HEAD_UNDER) root.classList.add('dvt-head-under');

  // Every key goes to the window while it is open. Escape shuts it; the rest
  // are stopped before any other window listener, and left to their default
  // so Tab, Enter and Space still work the buttons inside it.
  const onKey = (ev: KeyboardEvent): void => {
    if (!open) return;
    ev.stopImmediatePropagation();
    if (ev.key === 'Escape') {
      ev.preventDefault();
      setOpen(false);
    }
  };

  function setOpen(next: boolean): void {
    if (next === open) return;
    open = next;
    root.classList.toggle('hidden', !open);
    backdrop.classList.toggle('hidden', !open);
    if (open) paint(map.state());
    setMode('dvtable', open);
    config.onOpenChange?.(open);
  }

  function paint(state: DvMapState): void {
    if (!state.open && open) setOpen(false);
    elLive.classList.toggle('active', state.mode === 'live');
    elParking.classList.toggle('active', state.mode === 'parking');
    const height = heightText(state.originAltitudeKm);
    elFrom.textContent = state.originLabel === ''
      ? NOTHING
      : height === '' ? state.originLabel : `${state.originLabel}, ${height}`;
    elWhen.textContent = state.epochJd > 0 ? dateText(state.epochJd) : NOTHING;
    const movedText = state.moved === null ? '' : movedRowText(state.moved);
    elMoved.textContent = movedText;
    elMoved.classList.toggle('hidden', movedText === '');
    elNote.textContent = state.reason ?? '';
    elNote.classList.toggle('hidden', state.reason === null);

    const accelMps2 = config.accelMps2?.() ?? 0;
    const share = config.thrustShare?.() ?? 1;
    paintRows(elList, state.survey?.plans ?? [], state.selectedId, new Set(state.working),
      accelMps2, share, config.glossaryTip);

    const picked = state.selected;
    const steps = picked === null ? [] : planRows(picked, accelMps2, share);
    elPlan.classList.toggle('hidden', steps.length === 0);
    elPlanName.textContent = picked === null ? '' : plainBodyName(picked.destinationName).toUpperCase();
    paintPlan(elPlanList, steps, WIDE_PLAN_COLS);

    const skipped = state.survey?.skipped ?? [];
    elSkipped.replaceChildren();
    if (skipped.length > 0) {
      const skipHead = document.createElement('div');
      skipHead.className = 'dv-sub-head';
      skipHead.textContent = 'CANNOT GET THERE';
      tip(skipHead, SKIPPED_TIP);
      const grid = document.createElement('div');
      grid.className = 'dvt-skips';
      for (const skip of skipped) grid.append(skipLine(skip));
      elSkipped.append(skipHead, grid);
    }

    elAssume.replaceChildren();
    const head = document.createElement('div');
    head.className = 'dv-sub-head';
    head.textContent = 'WHAT THIS LEAVES OUT';
    elAssume.append(head);
    // Fixed player text, same for every survey; the tooltip on this whole
    // block carries the full `state.assumptions` list instead, rewrites included.
    const lines = LEAVES_OUT_LONG ? state.assumptions : TRANSFER_ASSUMPTIONS_SUMMARY;
    for (const line of lines) {
      const item = document.createElement('div');
      item.className = 'dv-assume';
      item.textContent = line;
      elAssume.append(item);
    }
    tip(elAssume, state.assumptions.join('\n'));
  }

  function paintTrip(): void {
    elFixRow.classList.toggle('hidden', fixText === NOTHING || fixText === '');
    elTrip.classList.toggle('hidden', !flying && elFixRow.classList.contains('hidden'));
  }

  const onListClick = (ev: MouseEvent): void => {
    const target = ev.target;
    if (!(target instanceof Element)) return;
    const row = target.closest<HTMLElement>('.dvt-row');
    if (!row) return;
    const id = row.dataset['dest'] ?? null;
    map.select(map.state().selectedId === id ? null : id);
  };
  const onClose = (): void => setOpen(false);
  const onLive = (): void => map.setMode('live');
  const onParking = (): void => map.setMode('parking');

  // Registered at boot rather than on open, so it sits ahead of every
  // capture-phase key listener added after it.
  window.addEventListener('keydown', onKey, true);
  elList.addEventListener('click', onListClick);
  elClose.addEventListener('click', onClose);
  backdrop.addEventListener('click', onClose);
  elLive.addEventListener('click', onLive);
  elParking.addEventListener('click', onParking);

  return {
    isOpen: () => open,
    setOpen,
    paint,
    setFlight(flight: DvMapFlight | null): void {
      const stepFace = flight === null || flight.ended || flight.skipStep === null || flight.skipSeconds <= 0
        ? '' : flight.nextStepLabel;
      const key = flight === null ? 'none'
        : `${flight.phase}|${flight.destinationName}|${Math.round(flight.dvToGoMps)}`
          + `|${Math.round(flight.nextEventS)}|${Math.round(flight.trimSpentMps)}`
          + `|${Math.round(flight.spentMps)}|${Math.round(flight.pricedDvMps)}|${flight.reason ?? ''}`
          + `|${stepFace}`;
      if (key === flyKey) return;
      flyKey = key;
      flying = flight !== null;
      for (const row of flyRows) row.classList.toggle('hidden', flight === null);
      if (flight !== null) {
        const set = (k: string, text: string): void => {
          const el = fly(k);
          if (el) el.textContent = text;
        };
        set('dest', flight.destinationName);
        set('phase', PHASE_WORDS[flight.phase]);
        set('dv', dvText(flight.dvToGoMps));
        set('next', durationText(flight.nextEventS));
        set('trim', dvText(flight.trimSpentMps));
        set('spent', spentText(flight.spentMps, flight.pricedDvMps));
        set('nextStep', stepFace);
      }
      elNextStepRow.classList.toggle('hidden', stepFace === '');
      elWhy.textContent = flight?.reason ?? '';
      elWhy.classList.toggle('hidden', flight === null || flight.reason === null);
      paintTrip();
    },
    setCorrection(text: string): void {
      if (text === fixText) return;
      fixText = text;
      elFix.textContent = text;
      paintTrip();
    },
    dispose(): void {
      setOpen(false);
      window.removeEventListener('keydown', onKey, true);
      elList.removeEventListener('click', onListClick);
      elClose.removeEventListener('click', onClose);
      backdrop.removeEventListener('click', onClose);
      elLive.removeEventListener('click', onLive);
      elParking.removeEventListener('click', onParking);
      elList.replaceChildren();
      elPlanList.replaceChildren();
      elSkipped.replaceChildren();
      elAssume.replaceChildren();
    },
  };
}
